<template>
  <v-card class="back" style="height: 500px">
    <v-layout fill-height>
      <v-flex >
        <v-layout column fill-height>
          <scroll-area>
            <template slot="header">
              <v-toolbar dark color="secondary" dense flat>
                <v-toolbar-title>
                  <span class="body-2 text-uppercase">{{ $t('rules for') }} "{{row.measureName || row.name}}"</span>
                </v-toolbar-title>
              </v-toolbar>
            </template>

            <template slot="body">
              <v-layout column fill-height justify-start class="pa-2">
                <div>
                  <v-alert :value="err" color="error" outline>{{err}}</v-alert>
                </div>
                <div v-for="(rule,name) in rowData" :key="name" class="rule">
                  <div style="display: flex; justify-content: space-between;">
                    <div>{{ $t(name) }}</div>
                    <v-btn small fab right color="primary" ma-1 class="supersmall" 
                    :data-testid="$options.name + '__btn__rowData' + name"
                    @click="rowData[name].push({wxpression:'new', message:'msg'})"><v-icon small>add</v-icon></v-btn>
                  </div>
                  <div v-if="rule && rule.length">
                    <v-layout v-for="(r,i) in rule" :key="'rule'+i" class="rule-item">
                      <v-flex mr-2 sm4>
                        <v-text-field hide-details
                          :label="$t('expression')"
                          :placeholder="$t('expression')"
                          :data-testid="$options.name + '__text-field__rule' + 'rule'+ i"
                          v-model="r.expression"
                        ></v-text-field>
                      </v-flex>
                      <v-flex sm8>
                        <v-text-field hide-details
                          :label="$t('message')"
                          :placeholder="$t('message')"
                          :data-testid="$options.name + '__text-field__message' + 'rule'+ i"
                          v-model="r.message"
                        ></v-text-field>
                      </v-flex>
                      <v-btn 
                      :data-testid="$options.name + '__btn__rowData' + 'close'"
                      @click="removeRuleRow(name, r.expression)" class="supersmall" fab right small color="secondary" ma-1><v-icon small>close</v-icon></v-btn>
                    </v-layout>
                  </div>
                </div>
              </v-layout>
            </template>

            <template slot="footer">
              <v-layout fill-height>
                <v-flex xs2>
                  <v-btn small 
                  :data-testid="$options.name + '__btn__' + 'confirmAndReload'"
                  @click="confirmAndReload" color="secondary">{{ $t('roll back') }}</v-btn>
                </v-flex>

                <v-layout fill-height justify-end>
                  <v-btn dark small 
                  :data-testid="$options.name + '__btn__' + 'close'"
                  @click="()=>{$emit('close')}" color="secondary">{{$t('cancel')}}</v-btn>
                  <v-btn small 
                  :data-testid="$options.name + '__btn__' + 'confirmAndSave'"
                  @click="confirmAndSave" color="success" >{{$t('save')}}</v-btn>
                </v-layout>
              </v-layout>
            </template>

          </scroll-area>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'

// Типы
interface RuleItem {
  expression: string
  message: string
}

interface RowData {
  [key: string]: RuleItem[]
}

interface RowProps {
  $id?: string
  source_id?: string
  measure_id?: string
  bridge?: string
  [key: string]: any
}

interface MetricItem {
  id: string
  [key: string]: any
}

interface BridgeItem {
  items?: Array<{ metrics?: MetricItem[] }>
  [key: string]: any
}

interface TemplateItem {
  id: string
  bridges?: Record<string, BridgeItem>
  [key: string]: any
}

interface ActiveDevice {
  template?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'RULES_TABLE',

  components: {
    scrollArea,
  },

  props: {
    row: {
      type: Object as () => RowProps,
      required: true,
      default: () => ({})
    },
    refId: {
      type: String,
      default: ''
    }
  },

  data(): {
    rowData: RowData
    err: string
  } {
    return {
      rowData: [],
      err: ''
    }
  },

  created(): void {
    this.fillData()
  },

  methods: {
    fillData(): void {
      const safeJSONParse = (entry: [string, any]): RuleItem[] => {
        const value = entry[1]
        if (typeof value === 'string' && value.length > 0 && value[0] !== '[') {
          this.err = `${this.$getTranslate('in field')} ${entry[0]} ${this.$getTranslate('an error has been fixed, please follow the rules')}`
          return [{ expression: value, message: '' }]
        }
        return JSON.parse(value || '[]')
      }

      const filtered = Object.entries(this.row).filter(
        e => e[0].length > 5 && e[0].substr(e[0].length - 5) === '_rule'
      )
      const parsed = filtered.map(e => [e[0], e[1] != null ? safeJSONParse(e) : []])
      this.rowData = Object.fromEntries(parsed)
    },

    removeRuleRow(name: string, expr: string): void {
      this.$dialog.confirm({
        text: 'Удалить выражение?',
        title: 'Удаление',
        actions: {
          false: 'Отмена',
          true: {
            color: 'red',
            text: 'Удалить',
            handle: () => {
              this.rowData[name] = this.rowData[name].filter(l => l.expression !== expr)
            }
          }
        }
      })
    },

    async saveData(): Promise<void> {
      try {
        const obj = await this.$bridge.getObjectById(this.row.$id)
        const d = Object.entries(this.rowData)
        const newRules = Object.fromEntries(d.map(e => [e[0], JSON.stringify(e[1])]))//.filter(v => v[1].length) // пустые не фильтруем, а то удаление не будет работать

        const u$ = await obj.update(newRules)
        if (u$) {
          this.$emit('polling')
        }
        console.log('OBJ', obj)
        console.log('NEW RULES', newRules)
        console.log('RULE UPDATE', u$)
        await this.$store.dispatch('loadMeasures', this.row.source_id)
      } catch (err) {
        this.$errorHandler(err)
      } finally {
        this.$emit('updateMeasures')
        this.$emit('close')
      }
    },

    async confirmAndSave(): Promise<void> {
      await this.$dialog.confirm({
        text: 'Сохранить данные?',
        title: 'Сохранение',
        actions: {
          false: 'Отмена',
          true: {
            color: 'green',
            text: 'Сохранить',
            handle: this.saveData
          }
        }
      })
    },

    async reloadDeviceData(): Promise<void> {
      try {
        const activeDevice = this.$store.state.devices.activeDevice as ActiveDevice
        const templ = activeDevice?.template
        const templates = await this.$bridge.getTemplates('device') as TemplateItem[]
        const d = templates.find(t => t.id === templ)

        if (d?.bridges && this.row.bridge) {
          // функция поиска совпадающих с шаблоном метрик
          // - в строке указан measure_id="slotState1" и ищется метрика с шаблоном "slotState${ref_id}"
          const funcFind = (metric: MetricItem): boolean => {
            const ref_id = this.refId
            return eval('`' + metric.id + '`') === this.row.measure_id
          }

          const bridge = d.bridges[this.row.bridge]
          const metrics = bridge?.items

          if (metrics) {
            metrics.forEach(item => {
              const m = item.metrics?.find(funcFind)
              if (m !== undefined) {
                let rules = Object.entries(m).filter(o => o[0].substr(o[0].length - 4) === 'Rule')
                rules = rules.map(r => [r[0].replace('Rule', '_rule'), r[1]])
                const rulesObj = Object.fromEntries(rules)

                Object.keys(this.rowData).forEach(key => {
                  this.rowData[key] = rulesObj[key] || []
                })
              }
            })
          }
        }
      } catch (err) {
        this.$errorHandler(err)
      }
    },

    async confirmAndReload(): Promise<void> {
      await this.$dialog.confirm({
        text: 'Вернуть настройки к значениям по умолчанию?',
        title: 'Подтверждение',
        actions: {
          false: 'Отмена',
          true: {
            color: 'green',
            text: 'Сбросить',
            handle: this.reloadDeviceData
          }
        }
      })
    },
  },
})
</script>

<style lang="stylus" scoped>
.rule
  border 1px solid #c1c163
  margin-bottom: 5px;
  padding: 5px;
.rule-item
  //border 1px solid red
  display flex
  justify-content stretch
  align-items baseline
  .field
    margin 0 3px
    outline: darkgray solid 1px;
    height: 30px;
    padding-left: 5px;
    line-height: 30px;
    background-color: white;
.v-btn.supersmall
  padding-top: 4px
  height: 24px
  width: 24px
  right: 5px
</style>
